import type { SpaceProvider } from "@/api/spaces/add.ts";
import type { EmbeddingStatus } from "@/api/spaces/files/list.ts";
import type { FileStatusCategory } from "@/shared/file-types.ts";
import type { SpaceEmbeddingPriorityName } from "@/api/util/sync.ts";
import type { operations } from "@/openapi.gen.ts";
import { $api } from "@/utils/fetch.ts";
import type {
	GetNextPageParamFunction,
	QueryClient,
} from "@tanstack/react-query";
import { keepPreviousData, useIsMutating } from "@tanstack/react-query";

export interface Space {
	id: string;
	name: string;
	canWrite: boolean;
	isSpaceAdmin: boolean;
	provider: SpaceProvider;
	autoEmbedOnUpload: boolean;
	autoEmbedOnSync: boolean;
	/**
	 * Standing preference for how this space's bulk embedding work is ordered
	 * against the org's other spaces. Space administrators only.
	 */
	embeddingPriority: SpaceEmbeddingPriorityName;
	syncStatus: string;
	lastSyncAt?: string;
	/**
	 * Active degradation reasons — the sync worked, but something the user
	 * relies on is not true. Empty means nothing to report. Carried on the space
	 * rather than only on the live sync-status query, because a degradation
	 * matters precisely while the space is idle and looking healthy.
	 */
	syncDegraded: string[];
	/** Whether the space is active. Deactivated spaces (C47) are paused. */
	isActive: boolean;
	/**
	 * Whether the CALLING user archived this space from their own view. Distinct
	 * from {@link Space.isActive}: archive is per-user and presentational, pause is
	 * org-wide and functional (#1871).
	 *
	 * Optional because only the LIST endpoint resolves it — the single-space GET
	 * has no per-user archive row to report, and every consumer that builds a
	 * `Space` from that response would otherwise have to invent a value. Absent
	 * therefore means "not known here", not "not archived"; read it as
	 * `archived ?? false` only where a boolean is genuinely required.
	 */
	archived?: boolean;
	/** Number of projects that link this space (used by deactivation dialog). */
	linkedProjectsCount: number;
	/**
	 * True when the connector's webhook subscription has been tombstoned
	 * (e.g. Microsoft AADSTS500014). The reconnect button gates on this for
	 * Microsoft connectors.
	 */
	subscriptionDisabled: boolean;
	/**
	 * Whether the current user created this space — the one person who may
	 * replace a connector package space's credentials.
	 *
	 * Optional because only the single-space GET derives it; absent means "not
	 * known here", which every consumer reads as "no".
	 */
	isCreator?: boolean;
	/**
	 * How often the space is synced on a schedule: its connector's interval and
	 * the space's own (null follows the connector's, 0 is off). Null when its
	 * connector does not sync on a schedule.
	 *
	 * Optional because only the single-space GET derives it; absent reads as null.
	 */
	scheduledSync?: { defaultHours: number; hours: number | null } | null;
	/**
	 * How the current user reached this space. `"linkedProject"` means they hold no
	 * permission on it and see it only because a project they belong to links it —
	 * the access the chat already had on their behalf. Such a space is read-only
	 * (`canWrite`/`isSpaceAdmin` are false) and absent from their own spaces list,
	 * so the page says why it is visible instead of looking like one of theirs.
	 */
	accessVia: "membership" | "linkedProject";
	/**
	 * How many files of this space are visible, when the inherited read reaches it
	 * through individual FILE links rather than a whole-space one. Null means the
	 * whole space is visible.
	 */
	visibleFileCount: number | null;
}

export interface SpaceFile {
	path: string;
	size: number;
	embeddingStatus: EmbeddingStatus;
	embeddingError: string | null;
	/** Classified cause; null on rows that failed before causes were recorded. */
	embeddingErrorCode: string | null;
	modifiedAt: string;
}

/**
 * The providers the caller actually holds spaces for — the Spaces browser's
 * filter offers only these, so no option can lead to an empty page.
 */
export const spaceProvidersQueryOptions = () =>
	$api.queryOptions("get", "/api/spaces/providers");

/** Sort fields accepted by GET /api/spaces. Defaults to name/asc server-side. */
export type SpaceSort = "name" | "createdAt" | "updatedAt";

/**
 * Archive filter accepted by GET /api/spaces. Per-user and presentational —
 * unrelated to the org-wide `isActive` pause. Defaults to `exclude` server-side.
 */
export type SpaceArchiveFilter = "exclude" | "only" | "all";

export const useSpacesInfiniteQuery = ({
	search = "",
	pageSize = 10,
	provider,
	sort,
	sortDir,
	archived,
	enabled = true,
	keepPrevious = false,
}: {
	search?: string;
	pageSize?: number;
	provider?: SpaceProvider;
	sort?: SpaceSort;
	sortDir?: "asc" | "desc";
	archived?: SpaceArchiveFilter;
	/** See `useProjectsInfiniteQuery` — for a caller mounted before it is visible. */
	enabled?: boolean;
	/** See `useProjectsInfiniteQuery` — keep the previous page while re-querying. */
	keepPrevious?: boolean;
}) => {
	const getNextPageParam: GetNextPageParamFunction<
		unknown,
		{
			spaces: Pick<
				Space,
				| "id"
				| "name"
				| "canWrite"
				| "provider"
				| "isSpaceAdmin"
				| "isActive"
				| "archived"
			>[];
			totalCount: number;
		}
	> = (lastPage, allPages, lastPageParam) => {
		const loadedCount = allPages.reduce(
			(sum, page) => sum + (page.spaces?.length || 0),
			0,
		);
		if (
			loadedCount < lastPage.totalCount &&
			typeof lastPageParam === "number"
		) {
			return lastPageParam + 1;
		}
		return undefined;
	};

	return $api.useInfiniteQuery(
		"get",
		"/api/spaces",
		{
			params: {
				query: {
					pageSize,
					search,
					provider,
					sort,
					sortDir,
					archived,
				},
			},
		},
		{
			pageParamName: "page",
			getNextPageParam,
			initialPageParam: 0,
			enabled,
			placeholderData: keepPrevious ? keepPreviousData : undefined,
		},
	);
};

/**
 * Archive or unarchive a space for the CALLING user only.
 *
 * Separate from `spaces/update.ts` on purpose: that one is space-admin gated
 * because everything on it is shared state, and archive is a personal view
 * preference (#1871). Callers must invalidate `get /api/spaces` — the archived
 * flag and the default `exclude` filter both come off that list.
 */
export const useSpaceArchiveMutation = () =>
	$api.useMutation("put", "/api/spaces/{spaceId}/archive");

export const spaceQueryOptions = (spaceId: string, enabled = true) =>
	$api.queryOptions(
		"get",
		"/api/spaces/{spaceId}",
		{ params: { path: { spaceId } } },
		{ enabled },
	);

// Projects that link this space (for direct project↔space navigation, P2-1).
// Scoped server-side to projects the user can access.
export const spaceLinkedProjectsQueryOptions = (spaceId: string) =>
	$api.queryOptions("get", "/api/spaces/{spaceId}/linked-projects", {
		params: {
			path: {
				spaceId,
			},
		},
	});

// Resource-side linking: set the projects that link this space (reconciled
// server-side against the projects the caller can write). Inverse of the
// project-side spaceLinks write on PUT /api/projects/{projectId}.
export const useUpdateSpaceLinkedProjectsMutation = () =>
	$api.useMutation("put", "/api/spaces/{spaceId}/linked-projects");

/**
 * Create a space. `gcTime: 0`: the request carries the credentials a person
 * typed into the wizard, and the answer can carry a webhook address — neither
 * stays in the cache once nothing shows the wizard.
 */
export const useCreateSpaceMutation = () =>
	$api.useMutation("post", "/api/spaces", { gcTime: 0 });

export const useDeleteSpaceMutation = () =>
	$api.useMutation("delete", "/api/spaces/{spaceId}");

export const useUpdateSpaceMutation = () =>
	$api.useMutation("put", "/api/spaces/{spaceId}");

// Space Teams Queries and Mutations
export const spaceTeamsQueryOptions = (spaceId: string) =>
	$api.queryOptions("get", "/api/spaces/{spaceId}/teams", {
		params: {
			path: {
				spaceId,
			},
		},
	});

export const downloadSpaceFileQueryOptions = (
	spaceId: string,
	path: string,
	convert?: boolean,
) =>
	$api.queryOptions("get", "/api/spaces/{spaceId}/files/download", {
		params: {
			path: {
				spaceId,
			},
			query: {
				path,
				convert,
			},
		},
		parseAs: "blob",
	});

// Verbatim extracted Markdown text of an embedded file (from the vision/OCR
// pipeline). 404s when the file has no extracted text yet (never embedded, or
// embedded before this feature) — callers surface a friendly empty state.
export const extractedTextQueryOptions = (spaceId: string, path: string) =>
	$api.queryOptions("get", "/api/spaces/{spaceId}/files/extracted", {
		params: {
			path: {
				spaceId,
			},
			query: {
				path,
			},
		},
	});

// What the file viewer and a chat's file citation show about one file — today
// the link to it in the provider's own web UI (`null` when there is none).
// Not retried: a citation can name a file that has since gone (404), and the
// link is an optional action — one request per hover, not three.
export const spaceFileInfoQueryOptions = (spaceId: string, path: string) =>
	$api.queryOptions(
		"get",
		"/api/spaces/{spaceId}/files/info",
		{
			params: {
				path: {
					spaceId,
				},
				query: {
					path,
				},
			},
		},
		{ retry: false },
	);

export const useAddSpaceTeamMutation = () =>
	$api.useMutation("post", "/api/spaces/{spaceId}/teams/add");

export const useUpdateSpaceTeamMutation = () =>
	$api.useMutation("post", "/api/spaces/{spaceId}/teams/add");

export const useRemoveSpaceTeamMutation = () =>
	$api.useMutation("post", "/api/spaces/{spaceId}/teams/remove");

// Space Users Queries and Mutations
export const spaceUsersQueryOptions = (spaceId: string) =>
	$api.queryOptions("get", "/api/spaces/{spaceId}/users", {
		params: {
			path: {
				spaceId,
			},
		},
	});

export const useAddSpaceUserMutation = () =>
	$api.useMutation("post", "/api/spaces/{spaceId}/users/add");

export const useUpdateSpaceUserMutation = () =>
	$api.useMutation("post", "/api/spaces/{spaceId}/users/add");

export const useRemoveSpaceUserMutation = () =>
	$api.useMutation("post", "/api/spaces/{spaceId}/users/remove");

// Space Files Queries and Mutations
export type SpaceFileSort = "path" | "size" | "modifiedAt" | "createdAt";

const getNextFilePageParam: GetNextPageParamFunction<
	unknown,
	{ files: SpaceFile[]; totalFiles: number; hasNextPage?: boolean }
> = (lastPage, _allPages, lastPageParam) => {
	if (lastPage.hasNextPage && typeof lastPageParam === "number") {
		return lastPageParam + 1;
	}
	return undefined;
};

export const useSpaceFilesInfiniteQuery = ({
	spaceId,
	search,
	pageSize = 20,
	sort,
	sortDir,
}: {
	spaceId: string;
	search?: string;
	pageSize?: number;
	sort?: SpaceFileSort;
	sortDir?: "asc" | "desc";
}) =>
	$api.useInfiniteQuery(
		"get",
		"/api/spaces/{spaceId}/files",
		{
			params: {
				path: { spaceId },
				query: { pageSize, search, sort, sortDir },
			},
		},
		{
			pageParamName: "page",
			getNextPageParam: getNextFilePageParam,
			initialPageParam: 0,
		},
	);

// Folder-level query: returns direct children (files + subfolders) for a given folder path.
// Each folder is cached independently, so navigating only fetches what's needed.
export const spaceFolderQueryOptions = (spaceId: string, folder: string) =>
	$api.queryOptions("get", "/api/spaces/{spaceId}/files/folder", {
		params: {
			path: { spaceId },
			query: { folder: folder || undefined },
		},
	});

/** The embedding-priority override configured for one folder: its own value, the
 * one it would inherit from an ancestor folder, and the space's own setting that
 * applies when neither is set. `enabled` is false until the menu that shows it is
 * actually opened — every folder in a listing would otherwise fetch its own. */
export const spaceFolderPriorityQueryOptions = (
	spaceId: string,
	folder: string,
	enabled: boolean,
) =>
	$api.queryOptions(
		"get",
		"/api/spaces/{spaceId}/files/folder-priority",
		{
			params: {
				path: { spaceId },
				query: { folder },
			},
		},
		{ enabled: enabled && folder.length > 0 },
	);

export const useUpdateFolderPriorityMutation = () =>
	$api.useMutation("put", "/api/spaces/{spaceId}/files/folder-priority");

// Flat cross-folder file listing with optional search + status category filter.
// Used by the search bar's "all folders" scope and by status-filter clicks from
// SpaceFileStatistics, which counts by the same category. pageSize is capped at
// 500 by the backend.
export const spaceFilesFlatQueryOptions = ({
	spaceId,
	search,
	statusCategory,
	pageSize = 500,
	enabled = true,
}: {
	spaceId: string;
	search?: string;
	statusCategory?: FileStatusCategory;
	pageSize?: number;
	enabled?: boolean;
}) =>
	$api.queryOptions(
		"get",
		"/api/spaces/{spaceId}/files",
		{
			params: {
				path: { spaceId },
				query: {
					pageSize,
					page: 0,
					search: search?.trim() ? search.trim() : undefined,
					statusCategory,
					sort: "path",
					sortDir: "asc",
				},
			},
		},
		{ enabled },
	);

// Server-side embedding status counts (avoids fetching all files for statistics).
export const spaceFileStatsQueryOptions = (spaceId: string) =>
	$api.queryOptions("get", "/api/spaces/{spaceId}/files/stats", {
		params: {
			path: { spaceId },
		},
	});

/**
 * Why the failed files in this space failed, grouped by cause.
 *
 * Its own query rather than a field on the stats above: the breakdown is read
 * when something has gone wrong, while the stats are polled throughout every
 * embedding run, and the two have no reason to share a cache entry.
 */
export const spaceFileErrorCodesQueryOptions = (spaceId: string) =>
	$api.queryOptions("get", "/api/spaces/{spaceId}/files/error-codes", {
		params: {
			path: { spaceId },
		},
	});

/** Invalidate all file-related queries for a space (folder views, stats, flat list). */
export const invalidateSpaceFiles = (
	queryClient: QueryClient,
	spaceId: string,
): void => {
	// Invalidate all folder queries for this space
	queryClient.invalidateQueries({
		predicate: (query) => {
			const key = query.queryKey;
			return (
				Array.isArray(key) &&
				key[0] === "get" &&
				(key[1] === "/api/spaces/{spaceId}/files/folder" ||
					key[1] === "/api/spaces/{spaceId}/files" ||
					key[1] === "/api/spaces/{spaceId}/files/stats" ||
					key[1] === "/api/spaces/{spaceId}/files/error-codes") &&
				JSON.stringify(key).includes(spaceId)
			);
		},
	});
};

// Single-file direct upload/update (used by code editor save)
export const useUploadSpaceFileMutation = () =>
	$api.useMutation("put", "/api/spaces/{spaceId}/files/upload");

export const useDeleteSpaceFileMutation = () =>
	$api.useMutation("delete", "/api/spaces/{spaceId}/files/delete");

export const useTransferSpaceFileMutation = () =>
	$api.useMutation("post", "/api/spaces/{spaceId}/files/transfer");

/**
 * Rename a file within its folder. Not a transfer: in a locally-stored space the
 * path is metadata, so the file keeps its bytes, its embeddings and its status.
 */
export const useRenameSpaceFileMutation = () =>
	$api.useMutation("post", "/api/spaces/{spaceId}/files/rename");

export const useEmbedSpaceFilesMutation = () =>
	$api.useMutation("post", "/api/spaces/{spaceId}/files/embed");

export const useCancelEmbedSpaceFilesMutation = () =>
	$api.useMutation("post", "/api/spaces/{spaceId}/files/cancel-embed");

/**
 * Re-queue every file in a space that failed for one particular reason.
 *
 * Distinct from {@link useEmbedSpaceFilesMutation}, which takes paths. The set
 * this addresses is the one that is too large to enumerate — a connection that
 * broke mid-run fails thousands of files for a reason that is nothing to do with
 * any of them.
 */
export const useRetrySpaceFileErrorsMutation = () =>
	$api.useMutation("post", "/api/spaces/{spaceId}/files/retry-errors");

// Space Sync and Link Mutations
export const useSyncSpaceMutation = () =>
	$api.useMutation("post", "/api/spaces/{spaceId}/sync");

export const syncStatusQueryOptions = (spaceId: string, enabled: boolean) =>
	$api.queryOptions(
		"get",
		"/api/spaces/{spaceId}/sync-status",
		{ params: { path: { spaceId } } },
		{ enabled, refetchInterval: enabled ? 5000 : false },
	);

export const useLinkSpaceMutation = () =>
	$api.useMutation("post", "/api/spaces/link");

/**
 * Reconnect an OAuth connector package's space with a new consent
 * (`capabilities.reconnect` on a package with `oauth`).
 */
export const useReconnectSpaceMutation = () =>
	$api.useMutation("post", "/api/spaces/{spaceId}/reconnect");

/**
 * Every credentials save — what a dialog watches to stay open while one runs.
 * `$api.useMutation` keys a mutation by `[method, path]` itself and takes no
 * other key; `spaces-service.credentials.test.tsx` pins that the two agree.
 */
export const reconnectSpaceCredentialsMutationKey = [
	"post",
	"/api/spaces/{spaceId}/credentials",
];

/**
 * Replace a credentials package space's secret values
 * (`capabilities.reconnect` on a package without OAuth).
 *
 * `gcTime: 0`: the request's variables carry the secret, and `reset()` only
 * detaches the form from the mutation — the mutation itself, secret included,
 * would stay in the cache for the default five minutes.
 */
export const useReconnectSpaceCredentialsMutation = () =>
	$api.useMutation("post", "/api/spaces/{spaceId}/credentials", {
		gcTime: 0,
	});

/**
 * The webhook address of the caller's own package connection — the one they
 * register by hand in the provider's dashboard. Creator-only, server-side.
 */
export const webhookAddressQueryOptions = (spaceId: string) =>
	$api.queryOptions("get", "/api/spaces/{spaceId}/webhook-address", {
		params: { path: { spaceId } },
	});

/**
 * Whether a credentials save is running — what keeps the surfaces around the
 * form (the toolbar dialog, the card's fold-out) from closing under it, which
 * would unmount the callbacks that report its outcome.
 */
export const useIsSavingCredentials = (): boolean =>
	useIsMutating({ mutationKey: reconnectSpaceCredentialsMutationKey }) > 0;

/**
 * The connection wizard a sandboxed connector package declares in its
 * manifest — fields, labels and whether it needs the consent redirect first.
 */
export const modularWizardQueryOptions = (provider: string) =>
	$api.queryOptions("get", "/api/spaces/modular/{provider}/wizard", {
		params: { path: { provider } },
	});

export type ModularWizard =
	operations["api_spaces_modular_provider_wizard_get"]["responses"]["200"]["content"]["application/json"];
export type ModularWizardField = ModularWizard["fields"][number];
export type ModularWizardValue = string | number | boolean | string[];
export type ModularWizardValues = Record<string, ModularWizardValue>;
export type ModularWizardOption =
	operations["api_spaces_modular_provider_options_post"]["responses"]["200"]["content"]["application/json"]["options"][number];

/**
 * One picker's choices. A query rather than a mutation so a tree that is
 * collapsed and reopened, or a drive picked twice, does not ask the package
 * again.
 */
export const modularOptionsQueryOptions = (
	provider: string,
	body: operations["api_spaces_modular_provider_options_post"]["requestBody"]["content"]["application/json"],
) =>
	$api.queryOptions("post", "/api/spaces/modular/{provider}/options", {
		params: { path: { provider } },
		body,
	});

/**
 * The connection settings an org admin may change on a connector package's
 * space — its folders — with their stored values. Empty for every other space.
 */
export const connectionSettingsQueryOptions = (spaceId: string) =>
	$api.queryOptions("get", "/api/spaces/{spaceId}/connection-settings", {
		params: { path: { spaceId } },
	});

export const useUpdateConnectionSettingsMutation = () =>
	$api.useMutation("put", "/api/spaces/{spaceId}/connection-settings");

/**
 * Whether a connection-settings save is running — what keeps the dialog around
 * the form from closing under it, as {@link useIsSavingCredentials} does. Keyed
 * as `$api.useMutation` keys the mutation above: `[method, path]`.
 */
export const useIsSavingConnectionSettings = (): boolean =>
	useIsMutating({
		mutationKey: ["put", "/api/spaces/{spaceId}/connection-settings"],
	}) > 0;

/**
 * The review step's preview a package computes. A query, keyed by the values it
 * ran against, so stepping back and forth to an unchanged review step does not
 * ask the package again.
 */
export const modularPreviewQueryOptions = (
	provider: string,
	body: operations["api_spaces_modular_provider_preview_post"]["requestBody"]["content"]["application/json"],
) =>
	$api.queryOptions("post", "/api/spaces/modular/{provider}/preview", {
		params: { path: { provider } },
		body,
	});

/**
 * Probe the connection a manifest wizard describes. A mutation, not a query:
 * it reaches a live server, and a result is only good for the values it ran
 * against.
 */
export const useTestModularConnectionMutation = () =>
	$api.useMutation("post", "/api/spaces/modular/{provider}/test");
